import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import { ClassroomStatus } from '../../../interfaces/activity';
import {
    updateSelectedActivity,
    selectSelectedActivity,
} from '../../../store/slices/selectedActivitySlice';
import useClassroomStatusPolling from './useClassroomStatusPolling';

const useCreateClassroomManagement = () => {
    const dispatch = useDispatch();
    const activity = useSelector(selectSelectedActivity)?.activity;
    const { addNotification } = useNotifications();
    const [submitting, setSubmitting] = useState(false);
    const [pollForClassroomUpdate, setPollForClassroomUpdate] = useState(false);

    const { pollLimitReached } = useClassroomStatusPolling(
        pollForClassroomUpdate,
        activity,
        dispatch,
        2000,
    );

    // ClassroomStatusPanel shows the timeout message in-context when limit is reached.
    useEffect(() => {
        if (pollLimitReached) {
            setPollForClassroomUpdate(false);
        }
    }, [pollLimitReached]);

    const handleSubmitEvent = async () => {
        setSubmitting(true);

        const activityData = {
            ...activity,
            classrooms_status: ClassroomStatus.READY_FOR_CLASSROOM_CREATION,
        };

        // Use triggerLoadingState: false to prevent the global loading spinner
        // which would unmount the entire page and scroll to top.
        // includeClassroomStatus: this is the only action allowed to write
        // classrooms_status; all other saves omit it from the payload.
        const result = await dispatch<any>(
            updateSelectedActivity(activity.pk, activityData, {
                triggerLoadingState: false,
                includeClassroomStatus: true,
            }),
        );
        if (result.isSuccessful) {
            setPollForClassroomUpdate(true);
            addNotification({
                id: `update-activity-with-class-update`,
                type: 'success',
                content: 'Classroom creation initiated.',
            });
        } else {
            addNotification({
                id: `update-activity-with-class-update`,
                type: 'error',
                content: 'An error occurred while updating the activity.',
            });
        }

        setSubmitting(false);
    };

    useEffect(() => {
        if (
            activity?.classrooms_status ===
            ClassroomStatus.READY_FOR_CLASSROOM_CREATION
        ) {
            setPollForClassroomUpdate(true);
        } else {
            setPollForClassroomUpdate(false);
        }

        // Cleanup
        return () => {
            setPollForClassroomUpdate(false);
        };
    }, [activity?.classrooms_status]);

    return {
        submitting,
        handleSubmitEvent,
        isPollingStopped: pollLimitReached,
    };
};

export default useCreateClassroomManagement;
